// Bearer-token plumbing between the auth layer and the plain-fetch API
// utils (workflow-api.ts, s3-upload.ts). Those utils are called from
// non-React code paths, so the current token is held at module level and
// kept fresh by TokenBridge (auth/RequireAuth.tsx) whenever the OIDC user
// changes or silently renews.
//
// The ID token (not the access token) is the bearer: it carries the
// custom:tenant_id claim and its `aud` matches what the API's JWT
// authorizer validates.

import { getTenantOverride } from '@/api/tenant-override';

let idToken: string | null = null;

export function setAuthToken(token: string | null): void {
  idToken = token;
}

// Spread into fetch headers for API Gateway calls: the bearer plus, for an
// admin who switched tenant, X-Reframer-Tenant. Every route resolves the
// tenant from these two (including /presigned-url, which refuses keys
// outside the resolved tenant's jobs), so the override must ride on every
// call, not only the /v1 ones apiFetch makes. Never attach these to
// presigned S3 URLs — S3 rejects requests carrying a foreign Authorization
// header alongside query-string auth.
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  const tenant = getTenantOverride();
  if (tenant) headers['X-Reframer-Tenant'] = tenant;
  return headers;
}

// The signed-in user's email, read from the ID token's payload (the token
// was already validated by the OIDC library; this only decodes it). Null
// when running authless or the token carries no email. Components use it
// to tell their own review notes from a colleague's.
export function currentUserEmail(): string | null {
  if (!idToken) return null;
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(atob(b64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const claims = JSON.parse(json) as { email?: unknown };
    return typeof claims.email === 'string' ? claims.email : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- /v1 calls

import { config } from '@/config';

export class ApiError extends Error {
  status: number;
  errors: string[];

  constructor(status: number, errors: string[]) {
    super(errors.join('; ') || `request failed (${status})`);
    this.status = status;
    this.errors = errors;
  }
}

// Fetch wrapper for the jobs API: base URL from build config, bearer +
// admin tenant override injected, JSON in/out, structured errors.
export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown } = {}
): Promise<T> {
  if (!config.apiEndpoint) {
    throw new ApiError(0, ['VITE_API_ENDPOINT is not configured']);
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders(),
  };

  const response = await fetch(`${config.apiEndpoint}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  const text = await response.text();
  let data: unknown = undefined;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    // non-JSON error body
  }
  if (!response.ok) {
    // Prefer our handlers' {errors:[…]}, then API Gateway's {message:…}
    // (e.g. authorizer rejections), then the raw body.
    const parsed = data as { errors?: string[]; message?: string } | undefined;
    const errors = parsed?.errors ?? [parsed?.message ?? text ?? response.statusText];
    throw new ApiError(response.status, errors);
  }
  return data as T;
}
